/**
 * TradeStack - Mileage Tracking Page
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Mileage.css';

function Mileage() {
  const { formatCurrency } = useConfig();

  const [mileageLogs, setMileageLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    yearToDate: { miles: 0, deduction: 0, tripCount: 0 },
    allTime: { miles: 0, deduction: 0, tripCount: 0 },
    currentRate: 0.70
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    tripDate: new Date().toISOString().split('T')[0],
    startLocation: '',
    endLocation: '',
    distanceMiles: '',
    isRoundTrip: false,
    purpose: '',
    jobId: ''
  });

  useEffect(() => {
    loadMileage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMileage = async () => {
    try {
      setLoading(true);
      const response = await api.getMileageLogs();
      setMileageLogs(response.data.mileageLogs || []);
      setStats({
        yearToDate: response.data.yearToDateStats || {},
        allTime: response.data.allTimeStats || {},
        currentRate: response.data.currentRate || 0.70
      });
    } catch (err) {
      setError('Failed to load mileage logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createMileageLog(formData);
      setShowAddForm(false);
      setFormData({
        tripDate: new Date().toISOString().split('T')[0],
        startLocation: '',
        endLocation: '',
        distanceMiles: '',
        isRoundTrip: false,
        purpose: '',
        jobId: ''
      });
      loadMileage();
    } catch (err) {
      console.error('Failed to add mileage:', err);
    }
  };

  const deleteMileage = async (id) => {
    if (!window.confirm('Delete this mileage log?')) return;
    try {
      await api.deleteMileageLog(id);
      loadMileage();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  return (
    <div className="mileage-page">
      <div className="page-header">
        <h1>Mileage Tracker</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Log Trip'}
        </button>
      </div>

      {/* Stats */}
      <div className="mileage-stats">
        <div className="stat-card">
          <div className="stat-icon">🚗</div>
          <div className="stat-content">
            <span className="stat-label">Year to Date</span>
            <span className="stat-value">
              {parseFloat(stats.yearToDate.total_miles || 0).toFixed(1)} mi
            </span>
            <span className="stat-sub">
              {stats.yearToDate.trip_count || 0} trips
            </span>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-label">YTD Deduction</span>
            <span className="stat-value">
              {formatCurrency(parseFloat(stats.yearToDate.total_deduction || 0))}
            </span>
            <span className="stat-sub">
              @ ${stats.currentRate}/mile
            </span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <span className="stat-label">All Time</span>
            <span className="stat-value">
              {parseFloat(stats.allTime.total_miles || 0).toFixed(1)} mi
            </span>
            <span className="stat-sub">
              {formatCurrency(parseFloat(stats.allTime.total_deduction || 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="add-mileage-form">
          <h3>Log New Trip</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  name="tripDate"
                  value={formData.tripDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Distance (miles)</label>
                <input
                  type="number"
                  name="distanceMiles"
                  value={formData.distanceMiles}
                  onChange={handleFormChange}
                  step="0.1"
                  placeholder="0.0"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Location</label>
                <input
                  type="text"
                  name="startLocation"
                  value={formData.startLocation}
                  onChange={handleFormChange}
                  placeholder="e.g., Office, Home"
                />
              </div>
              <div className="form-group">
                <label>End Location</label>
                <input
                  type="text"
                  name="endLocation"
                  value={formData.endLocation}
                  onChange={handleFormChange}
                  placeholder="e.g., Job Site"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Purpose</label>
              <input
                type="text"
                name="purpose"
                value={formData.purpose}
                onChange={handleFormChange}
                placeholder="e.g., Customer meeting, Material pickup"
              />
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  name="isRoundTrip"
                  checked={formData.isRoundTrip}
                  onChange={handleFormChange}
                />
                Round trip (doubles mileage)
              </label>
            </div>

            {formData.distanceMiles && (
              <div className="deduction-preview">
                <span>Estimated Deduction:</span>
                <strong>
                  {formatCurrency(
                    parseFloat(formData.distanceMiles || 0) *
                    (formData.isRoundTrip ? 2 : 1) *
                    stats.currentRate
                  )}
                </strong>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save Trip
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mileage List */}
      {loading ? (
        <div className="loading-state">Loading mileage logs...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : mileageLogs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚗</div>
          <h3>No mileage logged yet</h3>
          <p>Start tracking your business miles for tax deductions</p>
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            Log Your First Trip
          </button>
        </div>
      ) : (
        <div className="mileage-list">
          {mileageLogs.map(log => (
            <div key={log.id} className="mileage-card">
              <div className="mileage-date">
                <span className="date-day">
                  {new Date(log.trip_date).getDate()}
                </span>
                <span className="date-month">
                  {new Date(log.trip_date).toLocaleString('default', { month: 'short' })}
                </span>
              </div>
              <div className="mileage-route">
                <span className="route-text">
                  {log.start_location || 'Start'}
                  {' → '}
                  {log.end_location || 'Destination'}
                  {log.is_round_trip && ' (Round Trip)'}
                </span>
                {log.purpose && <span className="route-purpose">{log.purpose}</span>}
                {log.job_title && (
                  <Link to={`/jobs/${log.job_id}`} className="route-job">
                    {log.job_title}
                  </Link>
                )}
              </div>
              <div className="mileage-values">
                <span className="miles">{parseFloat(log.distance_miles).toFixed(1)} mi</span>
                <span className="deduction">{formatCurrency(log.deduction_amount)}</span>
              </div>
              <button
                className="delete-btn"
                onClick={() => deleteMileage(log.id)}
                title="Delete"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Mileage;
